import * as coreClient from "@azure/core-client";
import * as Parameters from "./models/parameters";
import * as Mappers from "./models/mappers";
import {
  HostOptions,
  CustomerDetailsAPIOptionalParams,
  CreateCustomerAccountOptionalParams,
  CreateCustomerAccountResponse,
  GetCustomerAccountOptionalParams,
  GetCustomerAccountResponse,
  UpdateCustomerAccountOptionalParams,
  UpdateCustomerAccountResponse,
  UploadCustomerAccountPhotoOptionalParams,
  UploadCustomerAccountPhotoResponse,
  CustomerAccountProfileImageOptionalParams,
  CustomerAccountProfileImageResponse,
  MobileAppVerifyOptionalParams,
  MobileAppVerifyResponse,
  CheckSwiftCardOptionalParams,
  CheckSwiftCardResponse,
  GetCustomerApplicantsOptionalParams,
  GetCustomerApplicantsResponse,
  GetApplicantSwiftcardsOptionalParams,
  GetApplicantSwiftcardsResponse
} from "./models";

export class CustomerDetailsAPI extends coreClient.ServiceClient {
  $host: HostOptions;

  /**
   * Initializes a new instance of the CustomerDetailsAPI class.
   * @param options The parameter options
   */
  constructor(options?: CustomerDetailsAPIOptionalParams) {
    // Initializing default values for options
    if (!options) {
      options = {};
    }
    const defaults: CustomerDetailsAPIOptionalParams = {
      requestContentType: "application/json; charset=utf-8"
    };

    const packageDetails = `azsdk-js-customerDetailsAPI/1.0.0-beta.1`;
    const userAgentPrefix =
      options.userAgentOptions && options.userAgentOptions.userAgentPrefix
        ? `${options.userAgentOptions.userAgentPrefix} ${packageDetails}`
        : `${packageDetails}`;

    const optionsWithDefaults = {
      ...defaults,
      ...options,
      userAgentOptions: {
        userAgentPrefix
      },
      baseUri:
        options.endpoint ??
        options.baseUri ??
        "https://nxbus.api.nationalexpress.com"
    };
    super(optionsWithDefaults);

    // Assigning values to Constant parameters
    this.$host = options.$host || "https://nxbus.api.nationalexpress.com";
  }

  /**
   * Create Customer Account Record
   * @param openIDToken The OpenID Token passed from Azure AD B2C
   * @param options The options parameters.
   */
  createCustomerAccount(
    openIDToken: string,
    options?: CreateCustomerAccountOptionalParams
  ): Promise<CreateCustomerAccountResponse> {
    return this.sendOperationRequest(
      { openIDToken, options },
      createCustomerAccountOperationSpec
    );
  }

  /**
   * Get Customer Account
   * @param openIDToken The OpenID Token passed from Azure AD B2C
   * @param options The options parameters.
   */
  getCustomerAccount(
    openIDToken: string,
    options?: GetCustomerAccountOptionalParams
  ): Promise<GetCustomerAccountResponse> {
    return this.sendOperationRequest(
      { openIDToken, options },
      getCustomerAccountOperationSpec
    );
  }

  /**
   * Update Customer Account
   * @param id The ID of the Customer Account
   * @param openIDToken The OpenID Token passed from Azure AD B2C
   * @param options The options parameters.
   */
  updateCustomerAccount(
    id: string,
    openIDToken: string,
    options?: UpdateCustomerAccountOptionalParams
  ): Promise<UpdateCustomerAccountResponse> {
    return this.sendOperationRequest(
      { id, openIDToken, options },
      updateCustomerAccountOperationSpec
    );
  }

  /**
   * Upload Customer Account Profile Image
   * @param id The ID of the Customer Account
   * @param options The options parameters.
   */
  uploadCustomerAccountPhoto(
    id: string,
    options?: UploadCustomerAccountPhotoOptionalParams
  ): Promise<UploadCustomerAccountPhotoResponse> {
    return this.sendOperationRequest(
      { id, options },
      uploadCustomerAccountPhotoOperationSpec
    );
  }

  /**
   * View Customer Account Profile Image
   * @param id The ID of the Customer Account
   * @param options The options parameters.
   */
  customerAccountProfileImage(
    id: string,
    options?: CustomerAccountProfileImageOptionalParams
  ): Promise<CustomerAccountProfileImageResponse> {
    return this.sendOperationRequest(
      { id, options },
      customerAccountProfileImageOperationSpec
    );
  }

  /**
   * Check Mobile App Account for Given Customer or Applicant
   * @param options The options parameters.
   */
  mobileAppVerify(
    options?: MobileAppVerifyOptionalParams
  ): Promise<MobileAppVerifyResponse> {
    return this.sendOperationRequest({ options }, mobileAppVerifyOperationSpec);
  }

  /**
   * Check Swift Card API
   * @param swiftCardNumber SwiftCard Number (Base64 Formatted String)
   * @param options The options parameters.
   */
  checkSwiftCard(
    swiftCardNumber: string,
    options?: CheckSwiftCardOptionalParams
  ): Promise<CheckSwiftCardResponse> {
    return this.sendOperationRequest(
      { swiftCardNumber, options },
      checkSwiftCardOperationSpec
    );
  }

  /**
   * Get All Applicants by Customer
   * @param billingCustomerEmail Billing Customer Email Address
   * @param options The options parameters.
   */
  getCustomerApplicants(
    billingCustomerEmail: string,
    options?: GetCustomerApplicantsOptionalParams
  ): Promise<GetCustomerApplicantsResponse> {
    return this.sendOperationRequest(
      { billingCustomerEmail, options },
      getCustomerApplicantsOperationSpec
    );
  }

  /**
   * Get Applicant Swiftcards
   * @param options The options parameters.
   */
  getApplicantSwiftcards(
    options?: GetApplicantSwiftcardsOptionalParams
  ): Promise<GetApplicantSwiftcardsResponse> {
    return this.sendOperationRequest(
      { options },
      getApplicantSwiftcardsOperationSpec
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const createCustomerAccountOperationSpec: coreClient.OperationSpec = {
  path: "/customers/V1",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.Customer
    },
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  requestBody: Parameters.body,
  urlParameters: [Parameters.$host],
  headerParameters: [
    Parameters.contentType,
    Parameters.accept,
    Parameters.openIDToken
  ],
  mediaType: "json",
  serializer
};
const getCustomerAccountOperationSpec: coreClient.OperationSpec = {
  path: "/customers/V1",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.Customer
    },
    404: {},
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.accept, Parameters.openIDToken],
  serializer
};
const updateCustomerAccountOperationSpec: coreClient.OperationSpec = {
  path: "/customers/V1/{id}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.Customer
    },
    404: {},
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  requestBody: Parameters.body1,
  urlParameters: [Parameters.$host, Parameters.id],
  headerParameters: [
    Parameters.contentType,
    Parameters.accept,
    Parameters.openIDToken
  ],
  mediaType: "json",
  serializer
};
const uploadCustomerAccountPhotoOperationSpec: coreClient.OperationSpec = {
  path: "/customers/V1/profile/{id}",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.CustomerProfileUploadResponse
    },
    400: {},
    404: {},
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  requestBody: Parameters.data,
  urlParameters: [Parameters.$host, Parameters.id],
  headerParameters: [Parameters.contentType1, Parameters.accept1],
  mediaType: "binary",
  serializer
};
const customerAccountProfileImageOperationSpec: coreClient.OperationSpec = {
  path: "/customers/V1/profile/{id}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: { type: { name: "Stream" }, serializedName: "parsedResponse" }
    },
    400: {},
    404: {},
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  urlParameters: [Parameters.$host, Parameters.id],
  headerParameters: [Parameters.accept2],
  serializer
};
const mobileAppVerifyOperationSpec: coreClient.OperationSpec = {
  path: "/customers/V1/mobileapp/verify",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.MobileAppVerifyStatus
    },
    404: {},
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  requestBody: Parameters.body2,
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.contentType, Parameters.accept],
  mediaType: "json",
  serializer
};
const checkSwiftCardOperationSpec: coreClient.OperationSpec = {
  path: "/customers/V1/swiftcard/check",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.SwiftCardCheckStatus
    },
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.accept, Parameters.swiftCardNumber],
  serializer
};
const getCustomerApplicantsOperationSpec: coreClient.OperationSpec = {
  path: "/customers/V1/applicants",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: {
        type: {
          name: "Sequence",
          element: {
            type: { name: "Composite", className: "ApplicantCustomer" }
          }
        }
      }
    },
    404: {},
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.accept, Parameters.billingCustomerEmail],
  serializer
};
const getApplicantSwiftcardsOperationSpec: coreClient.OperationSpec = {
  path: "/customers/V1/applicants/swiftcards",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: {
        type: {
          name: "Sequence",
          element: {
            type: { name: "Composite", className: "CustomerSwiftCard" }
          }
        }
      }
    },
    404: {},
    default: {
      bodyMapper: Mappers.ApiError
    }
  },
  requestBody: Parameters.body3,
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.contentType, Parameters.accept],
  mediaType: "json",
  serializer
};
